import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PROPOSAL_STATUSES,
  type ProposalDto,
  type ProposalStatus,
} from '@shared';
import { apiGet, ApiError } from '../../lib/api';
import { ProposalStatusChip } from '../../components/ProposalStatusChip';

type Filter = 'all' | ProposalStatus;

const STATUS_LABEL: Record<ProposalStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
};

// Pence → display string in the proposal's currency.
function formatMoney(amount_pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount_pence / 100);
  } catch {
    return `${currency} ${(amount_pence / 100).toLocaleString()}`;
  }
}

export function AdminProposals() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ProposalDto[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ proposals: ProposalDto[] }>('/api/proposals')
      .then((r) => setRows(r.proposals))
      .catch((err: Error) => {
        const msg = err instanceof ApiError ? err.serverMessage : err.message;
        setError(msg ?? 'Failed to load proposals.');
      });
  }, []);

  const list = useMemo(
    () => (rows ?? []).filter((p) => filter === 'all' ? true : p.status === filter),
    [rows, filter],
  );

  const counts = useMemo(() => {
    const c: Record<ProposalStatus, number> = {
      draft: 0, sent: 0, accepted: 0, declined: 0, withdrawn: 0, expired: 0,
    };
    for (const p of rows ?? []) c[p.status] = (c[p.status] ?? 0) + 1;
    return c;
  }, [rows]);

  return (
    <>
      <div>
        <h1 className="portal-h1">Proposals</h1>
        <p className="portal-sub">
          Drafts in flight, sent proposals awaiting client decision, and accepted engagements ready
          to move into delivery.
        </p>
      </div>

      <div className="portal-cards">
        <div className="card">
          <div className="lbl">Drafts</div>
          <div className="val">{counts.draft}</div>
          <div className="delta">In editing</div>
        </div>
        <div className="card">
          <div className="lbl">Sent</div>
          <div className="val">{counts.sent}</div>
          <div className="delta">Awaiting client decision</div>
        </div>
        <div className="card">
          <div className="lbl">Accepted</div>
          <div className="val">{counts.accepted}</div>
          <div className="delta">Engagement live</div>
        </div>
        <div className="card">
          <div className="lbl">Declined / withdrawn</div>
          <div className="val">{counts.declined + counts.withdrawn + counts.expired}</div>
          <div className="delta">Closed without engagement</div>
        </div>
      </div>

      <div className="tabs" style={{ marginTop: 24 }}>
        {(['all', ...PROPOSAL_STATUSES] as Filter[]).map((t) => (
          <button key={t} className={filter === t ? 'on' : ''} onClick={() => setFilter(t)}>
            {t === 'all' ? 'All' : STATUS_LABEL[t]}
          </button>
        ))}
      </div>

      <div className="card flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Client</th>
              <th>Brief</th>
              <th>Plan</th>
              <th className="amt">Fee</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={7} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
            )}
            {error && (
              <tr><td colSpan={7} style={{ color: 'var(--red)', fontSize: 13 }}>{error}</td></tr>
            )}
            {rows !== null && !error && list.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No proposals in this view.
                </td>
              </tr>
            )}
            {list.map((p) => (
              <tr
                key={p.id}
                className="row-clickable"
                tabIndex={0}
                onClick={() => navigate(`/admin/proposals/${p.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/admin/proposals/${p.id}`);
                  }
                }}
              >
                <td><span className="rid">{p.id}</span></td>
                <td>{p.client_name ?? p.client_id}</td>
                <td style={{ fontSize: 13, color: 'var(--fg-soft)', fontFamily: 'var(--mono)' }}>
                  {p.request_id}
                </td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>
                  {p.payment_plan.replace(/_/g, '/')}
                </td>
                <td className="amt" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(p.fee_pence, p.currency)}
                </td>
                <td><ProposalStatusChip status={p.status} /></td>
                <td style={{ color: 'var(--fg-mute)', fontSize: 12, fontFamily: 'var(--mono)' }}>
                  {p.updated_at.slice(0, 10)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
